import React from "react";

/**
 * Underline tabs — the channel tabs on the list screen and the two tabs on
 * the detail screen.
 *
 * The underline is a 2px bottom border on the button itself, pulled up by
 * `-mb-px` so it sits ON the container's hairline rather than below it.
 * Inactive tabs carry a transparent border of the same width so selecting
 * one never shifts the row by a pixel.
 *
 * @param {Object} props
 * @param {string} props.value - Id of the selected tab.
 * @param {(id: string) => void} props.onChange - Called with the newly selected id.
 * @param {Array<{ id: string, label: React.ReactNode, count?: number|null }>} props.items - Tabs, in order.
 * @param {string} props.label - Accessible name for the tablist.
 * @param {string} [props.idPrefix='bc-tab'] - Prefix for the generated tab ids.
 * @param {string} [props.className] - Extra classes on the tablist.
 */
export const Tabs = ({
  value,
  onChange,
  items = [],
  label,
  idPrefix = "bc-tab",
  className = "",
}) => {
  /** ArrowLeft/ArrowRight move between tabs and wrap at both ends. */
  const handleKeyDown = (event, index) => {
    const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (step === 0 || items.length === 0) return;
    event.preventDefault();
    const next = items[(index + step + items.length) % items.length];
    if (next) onChange?.(next.id);
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className={`flex gap-0.5 border-b border-bc-border ${className}`}
    >
      {items.map((item, index) => {
        const isActive = item.id === value;
        // A count of 0 is a result and prints; a missing count prints nothing
        // at all, because a fabricated 0 reads as "we counted, and found none".
        const hasCount = item.count !== null && item.count !== undefined;

        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`${idPrefix}-${item.id}`}
            aria-selected={isActive}
            aria-controls={`${idPrefix}-${item.id}-panel`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange?.(item.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={`-mb-px inline-flex items-center gap-1.5 px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-inset ${
              isActive
                ? "text-bc-text font-semibold border-b-2 border-orange-primary"
                : "text-bc-soft border-b-2 border-transparent hover:text-bc-text-2"
            }`}
          >
            <span>{item.label}</span>
            {hasCount ? (
              <span className="font-mono tabular-nums text-bc-faint">{item.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};
